import { inject, Injectable } from '@angular/core'
import { PRODUCT_CONFIGURATION, type ProductCapability } from '@awork/_shared/services/product/product-configuration'
import type { Notification } from '@awork/framework/models/notification.model'
import type { NotificationEvents } from '@awork/framework/models/notification.types'
import type { TransientEvents } from '@awork/_shared/models/transient-events.model'
import {
  supportsaworkAINotification,
  supportsaworkAINotificationEvent,
  supportsaworkAITransientNotification
} from './product-notifications.utils'

/** Product support is separate from permissions, entitlements, and rollout flags. */
@Injectable({ providedIn: 'root' })
export class ProductCapabilities {
  private readonly configuration = inject(PRODUCT_CONFIGURATION)
  readonly isaworkAI = this.configuration.id === 'aworkAI'
  /** Default landing page for this product. */
  readonly homeRoute = this.configuration.homeRoute
  /** Avoids returning to the AI home when AI access is denied. */
  readonly agentAccessDeniedRoute = this.isaworkAI ? '/not-allowed' : this.homeRoute

  readonly branding = this.configuration.branding
  readonly tracking = this.configuration.tracking
  private readonly logoPrefix = this.isaworkAI ? 'images/visuals/logo-aworkAI' : 'images/visuals/logo'
  readonly images = {
    logo: `${this.logoPrefix}.svg`,
    logoWhite: `${this.logoPrefix}-white.svg`,
    logoCropped: `${this.logoPrefix}-cropped.svg`,
    logoCroppedWhite: `${this.logoPrefix}-cropped-white.svg`,
    logoNew: `${this.logoPrefix}-new.svg`,
    logoNewWhite: `${this.logoPrefix}-new-white.svg`,
    logoWhiteOnBlue: `${this.logoPrefix}-white-on-blue.svg`
  } as const

  /** Logo widths at the navigation and login artwork heights. */
  readonly logoDimensions = {
    navigationWidth: this.isaworkAI ? (330 / 72) * 25 : 90,
    authWidth: this.isaworkAI ? (330 / 72) * 56 : 203,
    croppedWidth: ((this.isaworkAI ? 60 : 59) / 72) * 25
  } as const

  /**
   * Reads links after the app has loaded its translations.
   * @returns Links for the selected product and language.
   */
  get links() {
    return this.isaworkAI
      ? q.translations.productConfiguration.aworkAI.links
      : q.translations.productConfiguration.awork.links
  }

  /**
   * Checks whether an event is available in this product, including its settings row.
   * @param {NotificationEvents} event Notification event.
   * @returns {boolean} Whether the event is supported.
   */
  supportsNotificationEvent(event: NotificationEvents): boolean {
    return !this.isaworkAI || supportsaworkAINotificationEvent(event)
  }

  /**
   * Applies product visibility without changing delivery preferences or read state.
   * @param {Notification} notification Notification to display.
   * @returns {boolean} Whether the notification belongs in this product.
   */
  supportsNotification(notification: Notification): boolean {
    return !this.isaworkAI || supportsaworkAINotification(notification)
  }

  /**
   * Filters visible notifications and omits originals with an active snoozed reminder.
   * @param {Notification[]} notifications Notifications from the shared query.
   * @returns {Notification[]} Notifications to display and count.
   */
  filterNotifications(notifications: Notification[]): Notification[] {
    const supported = notifications.filter(notification => this.supportsNotification(notification))
    const snoozedIds = new Set(supported.map(notification => notification.reminderOfNotificationId).filter(Boolean))

    return supported.filter(notification => !snoozedIds.has(notification.id))
  }

  /**
   * Checks whether this product supports a transient prompt.
   * @param {TransientEvents} event Transient prompt event.
   * @returns {boolean} Whether the prompt is supported.
   */
  supportsTransientNotification(event: TransientEvents): boolean {
    return !this.isaworkAI || supportsaworkAITransientNotification(event)
  }
  /**
   * Checks whether the selected product supports a surface.
   * @param {ProductCapability} capability Capability to check.
   * @returns {boolean} Whether the product supports the surface.
   */
  supports(capability: ProductCapability): boolean {
    return this.configuration.capabilities[capability]
  }
}
